import { useMemo, useState } from "react";
import { Sparkles, ChevronDown, ChevronRight, Paperclip, FileText } from "lucide-react";
import { resumoUso, temPreco, fmtMoeda, MOEDA } from "../lib/usoIa";
import { somarDias } from "../lib/abordagem";
import { fmtShort } from "../utils";

// Consumo da IA, para quem paga a conta.
//
// Não mostra SALDO: a OpenAI não expõe saldo por chave de API, e inventar um
// número aqui seria pior do que não ter. Mostra o que dá para saber de verdade
// — quantas análises rodaram, quantos tokens, e quanto cada uma custa em média.
// Com o preço configurado, responde a pergunta que importa: quantas análises
// ainda cabem no crédito que você vai colocar.

export default function UsoIa({ partners = [], hoje, role = "comercial" }) {
  const [aberto, setAberto] = useState(false);
  const [credito, setCredito] = useState("");
  const gerencial = role === "gerente" || role === "admin";

  const mes = useMemo(() => resumoUso(partners, somarDias(hoje, -30)), [partners, hoje]);
  const tudo = useMemo(() => resumoUso(partners), [partners]);

  if (!gerencial || tudo.total === 0) return null;
  const restantes = credito ? mes.estimarRestantes(Number(credito)) : null;

  return (
    <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", borderLeft: "4px solid #7c3aed", padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Sparkles size={15} color="#7c3aed" />
        <div style={{ flex: 1, minWidth: 190 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>
            {mes.total} {mes.total === 1 ? "análise" : "análises"} nos últimos 30 dias
          </div>
          <div style={{ fontSize: 10.5, color: "#64748b" }}>
            {mes.tokens.toLocaleString("pt-BR")} tokens · média de {mes.mediaTokens.toLocaleString("pt-BR")} por análise
            {mes.custo != null && ` · ${fmtMoeda(mes.custo)}`}
            {mes.custoMedio != null && ` (${fmtMoeda(mes.custoMedio)} cada)`}
          </div>
        </div>
        <button onClick={() => setAberto((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
          {aberto ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Detalhe
        </button>
      </div>

      {aberto && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f1f5f9" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8, marginBottom: 12 }}>
            <Bloco icone={<FileText size={12} />} cor="#0d9488" n={mes.porTipo.conversa} label="sobre conversa exportada" />
            <Bloco icone={<Paperclip size={12} />} cor="#f59e0b" n={mes.porTipo.print} label="sobre print (imagem)" />
            <Bloco icone={<Sparkles size={12} />} cor="#64748b" n={tudo.total} label="desde o início" />
          </div>

          {/* Imagem custa muito mais que texto. É o argumento concreto para a
              equipe preferir o export em vez do print. */}
          {mes.porTipo.print > 0 && mes.porTipo.conversa > 0 && (
            <div style={{ fontSize: 10.5, color: "#0f766e", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 7, padding: "7px 10px", marginBottom: 10, lineHeight: 1.45 }}>
              Análise de imagem consome bem mais token que a de texto. Quanto mais a equipe usar o
              <strong> export da conversa</strong> no lugar do print, mais análise cabe no mesmo crédito.
            </div>
          )}

          {temPreco() ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: "#475569" }}>Se eu colocar {MOEDA === "BRL" ? "R$" : "US$"}</span>
              <input
                value={credito} onChange={(e) => setCredito(e.target.value.replace(/[^\d.]/g, ""))}
                placeholder="20" inputMode="decimal"
                style={{ width: 90, padding: "6px 9px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 12, color: "#334155" }}
              />
              <span style={{ fontSize: 11, color: "#475569" }}>
                de crédito, dá para
                <strong style={{ color: "#0f172a" }}> {restantes != null ? `${restantes.toLocaleString("pt-BR")} análises` : "—"}</strong>
                {" "}nesse ritmo.
              </span>
            </div>
          ) : (
            <div style={{ fontSize: 10.5, color: "#94a3b8", lineHeight: 1.45 }}>
              O custo em dinheiro não aparece porque o preço do modelo não está configurado
              (<code>REACT_APP_OPENAI_PRECO_ENTRADA</code> e <code>_SAIDA</code>, por milhão de tokens).
              Sem isso o painel mostra tokens e não inventa valor — número de custo errado é pior do que nenhum.
            </div>
          )}

          {mes.ultima && (
            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 8 }}>Última análise em {fmtShort(mes.ultima)}.</div>
          )}
        </div>
      )}
    </div>
  );
}

function Bloco({ icone, cor, n, label }) {
  return (
    <div style={{ padding: "9px 11px", borderRadius: 8, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 5, color: cor }}>
        {icone}<span style={{ fontSize: 17, fontWeight: 800 }}>{n}</span>
      </div>
      <div style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>{label}</div>
    </div>
  );
}
